import React, { useState, useEffect } from 'react';
import NeonProgress from '../ui/NeonProgress';
import { formatUploadBytes } from '../../lib/uploadVideo';

export interface QuickUploadConfig {
  codec: 'h264' | 'h265';
  targetSizeMB?: number;
  width?: number;
  height?: number;
  normalizeAudio?: boolean;
  lockAspect?: boolean;
}

interface QuickConfigModalProps {
  files: File[];
  isOpen: boolean;
  onConfirm: (config: QuickUploadConfig) => void;
  onCancel: () => void;
  /** Progresso agregado enquanto envia (0–100 e bytes) */
  uploadProgress?: {
    currentIndex: number;
    totalFiles: number;
    loaded: number;
    total: number;
    fileName?: string;
  } | null;
  isUploading?: boolean;
}

const QuickConfigModal: React.FC<QuickConfigModalProps> = ({
  files,
  isOpen,
  onConfirm,
  onCancel,
  uploadProgress = null,
  isUploading = false,
}) => {
  const [config, setConfig] = useState<QuickUploadConfig>({
    codec: 'h264',
    normalizeAudio: false,
    lockAspect: true,
  });

  const [targetSizeInput, setTargetSizeInput] = useState('');
  const [widthInput, setWidthInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [errors, setErrors] = useState<{ targetSize?: string; dim?: string }>({});

  useEffect(() => {
    if (isOpen && !isUploading) {
      setConfig({ codec: 'h264', normalizeAudio: false, lockAspect: true });
      setTargetSizeInput('');
      setWidthInput('');
      setHeightInput('');
      setErrors({});
    }
  }, [isOpen, isUploading]);

  const validateTargetSize = (value: string) => {
    if (value === '') return true;

    const size = parseInt(value);
    if (isNaN(size) || size < 1) {
      setErrors(prev => ({ ...prev, targetSize: 'Tamanho deve ser um número válido maior que 0' }));
      return false;
    }

    if (size > 1000) {
      setErrors(prev => ({ ...prev, targetSize: 'Tamanho não pode exceder 1000 MB' }));
      return false;
    }

    const largestFile = Math.max(...files.map(f => f.size));
    const largestMB = largestFile / (1024 * 1024);

    if (size >= largestMB) {
      setErrors(prev => ({ ...prev, targetSize: 'Tamanho deve ser menor que o maior arquivo original' }));
      return false;
    }

    setErrors(prev => ({ ...prev, targetSize: undefined }));
    return true;
  };

  const validateDims = (w: string, h: string) => {
    const parse = (v: string) => (v === '' ? undefined : parseInt(v, 10));
    const width = parse(w);
    const height = parse(h);
    if (width !== undefined && (width < 16 || width > 7680)) {
      setErrors(prev => ({ ...prev, dim: 'Largura entre 16 e 7680' }));
      return false;
    }
    if (height !== undefined && (height < 16 || height > 7680)) {
      setErrors(prev => ({ ...prev, dim: 'Altura entre 16 e 7680' }));
      return false;
    }
    setErrors(prev => ({ ...prev, dim: undefined }));
    return true;
  };

  const handleTargetSizeChange = (value: string) => {
    setTargetSizeInput(value);
    validateTargetSize(value);
  };

  const handleWidthChange = (value: string) => {
    setWidthInput(value);
    if (config.lockAspect !== false && value && heightInput === '') {
      // sem original global no rápido — só valida
    }
    validateDims(value, heightInput);
  };

  const handleHeightChange = (value: string) => {
    setHeightInput(value);
    validateDims(widthInput, value);
  };

  const handleConfirm = () => {
    if (!validateTargetSize(targetSizeInput)) return;
    if (!validateDims(widthInput, heightInput)) return;

    const width = widthInput ? parseInt(widthInput, 10) : undefined;
    const height = heightInput ? parseInt(heightInput, 10) : undefined;

    const finalConfig: QuickUploadConfig = {
      codec: config.codec,
      targetSizeMB: targetSizeInput ? parseInt(targetSizeInput) : undefined,
      width,
      height,
      normalizeAudio: !!config.normalizeAudio,
    };

    onConfirm(finalConfig);
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const getTotalSize = () => {
    const total = files.reduce((sum, file) => sum + file.size, 0);
    return formatFileSize(total);
  };

  const getCompressionEstimate = () => {
    if (!targetSizeInput) return null;

    const targetSize = parseInt(targetSizeInput);
    const largestFile = Math.max(...files.map(f => f.size));
    const largestMB = largestFile / (1024 * 1024);
    const ratio = ((largestMB - targetSize) / largestMB) * 100;

    return ratio > 0 ? ratio.toFixed(1) : null;
  };

  if (!isOpen) return null;

  const pct =
    uploadProgress && uploadProgress.total > 0
      ? Math.round((uploadProgress.loaded / uploadProgress.total) * 100)
      : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="retro-card max-w-md w-full border-4 border-retro-primary shadow-retro-2">
        <div className="p-4 border-b-2 border-retro-line bg-retro-accent">
          <h2 className="text-retro-pixel text-lg text-black">
            CONFIGURAÇÃO RÁPIDA
          </h2>
          <p className="text-sm text-black mt-1">
            {files.length} arquivo{files.length > 1 ? 's' : ''} • {getTotalSize()}
          </p>
        </div>

        <div className="p-4 space-y-4">
          {isUploading && uploadProgress ? (
            <div className="space-y-3">
              <p className="text-sm text-retro-text font-mono">
                Enviando {uploadProgress.currentIndex}/{uploadProgress.totalFiles}
                {uploadProgress.fileName ? `: ${uploadProgress.fileName}` : ''}
              </p>
              <div className="flex justify-between text-xs text-retro-muted font-mono">
                <span>Upload</span>
                <span>
                  {formatUploadBytes(uploadProgress.loaded)} / {formatUploadBytes(uploadProgress.total)} ({pct}%)
                </span>
              </div>
              <NeonProgress
                value={pct}
                variant="upload"
                label="Progresso do upload"
                showPercentage={false}
                animated
              />
            </div>
          ) : (
            <>
              <div className="retro-card p-3 bg-retro-bg border-2 border-retro-line">
                <h3 className="text-retro-pixel text-xs text-retro-accent mb-2">
                  ARQUIVOS SELECIONADOS
                </h3>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {files.map((file, index) => (
                    <div key={index} className="flex justify-between text-xs">
                      <span className="text-retro-text truncate flex-1 mr-2">
                        {file.name}
                      </span>
                      <span className="text-retro-muted">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-retro-accent mb-2 font-pixel text-xs tracking-wide">
                  CODEC DE VÍDEO
                </label>
                <div className="space-y-2">
                  <label className={`
                    retro-card p-3 border-2 cursor-pointer transition-all duration-200 flex items-center gap-3
                    ${config.codec === 'h264'
                      ? 'border-retro-primary bg-retro-primary bg-opacity-10'
                      : 'border-retro-line hover:border-retro-accent'
                    }
                  `}>
                    <input
                      type="radio"
                      name="codec"
                      value="h264"
                      checked={config.codec === 'h264'}
                      onChange={(e) => setConfig(prev => ({ ...prev, codec: e.target.value as 'h264' | 'h265' }))}
                    />
                    <div>
                      <div className={`text-sm font-medium ${config.codec === 'h264' ? 'text-black' : 'text-retro-text'}`}>
                        H.264 (AVC)
                      </div>
                      <div className={`text-xs ${config.codec === 'h264' ? 'text-black' : 'text-retro-muted'}`}>
                        Máxima compatibilidade
                      </div>
                    </div>
                  </label>

                  <label className={`
                    retro-card p-3 border-2 cursor-pointer transition-all duration-200 flex items-center gap-3
                    ${config.codec === 'h265'
                      ? 'border-retro-primary bg-retro-primary bg-opacity-10'
                      : 'border-retro-line hover:border-retro-accent'
                    }
                  `}>
                    <input
                      type="radio"
                      name="codec"
                      value="h265"
                      checked={config.codec === 'h265'}
                      onChange={(e) => setConfig(prev => ({ ...prev, codec: e.target.value as 'h264' | 'h265' }))}
                    />
                    <div>
                      <div className={`text-sm font-medium ${config.codec === 'h265' ? 'text-black' : 'text-retro-text'}`}>
                        H.265 (HEVC)
                      </div>
                      <div className={`text-xs ${config.codec === 'h265' ? 'text-black' : 'text-retro-muted'}`}>
                        Melhor eficiência
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm text-retro-accent mb-2 font-pixel text-xs tracking-wide">
                  TAMANHO ALVO (MB) — opcional
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={targetSizeInput}
                  onChange={(e) => handleTargetSizeChange(e.target.value)}
                  className={`retro-input w-full px-3 py-2 ${errors.targetSize ? 'border-retro-danger' : ''}`}
                  placeholder="Vazio = compressão automática"
                />
                {errors.targetSize && (
                  <p className="text-retro-danger text-xs mt-1">❌ {errors.targetSize}</p>
                )}

                {getCompressionEstimate() && (
                  <div className="retro-card p-2 mt-2 bg-retro-accent bg-opacity-10 border-2 border-retro-accent">
                    <p className="text-xs text-retro-text">
                      📊 Compressão estimada:{' '}
                      <span className="text-black font-medium">{getCompressionEstimate()}%</span>
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-retro-accent mb-2 font-pixel text-xs tracking-wide">
                  RESOLUÇÃO ALVO (px) — opcional
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="16"
                    max="7680"
                    value={widthInput}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    className="retro-input w-full px-3 py-2"
                    placeholder="Largura"
                  />
                  <span className="text-retro-muted">×</span>
                  <input
                    type="number"
                    min="16"
                    max="7680"
                    value={heightInput}
                    onChange={(e) => handleHeightChange(e.target.value)}
                    className="retro-input w-full px-3 py-2"
                    placeholder="Altura"
                  />
                </div>
                {errors.dim && (
                  <p className="text-retro-danger text-xs mt-1">❌ {errors.dim}</p>
                )}
                <p className="text-retro-muted text-xs mt-1">
                  Um lado só preserva proporção no encode
                </p>
              </div>

              <label className="flex items-start gap-2 cursor-pointer retro-card p-3 border-2 border-retro-line">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!config.normalizeAudio}
                  onChange={(e) => setConfig(prev => ({ ...prev, normalizeAudio: e.target.checked }))}
                />
                <span>
                  <span className="text-sm text-retro-text block">Normalizar volume</span>
                  <span className="text-xs text-retro-muted">
                    Deixar perto de 0 dB sem estourar
                  </span>
                </span>
              </label>
            </>
          )}
        </div>

        {!isUploading && (
          <>
            <div className="p-4 border-t-2 border-retro-line flex gap-3">
              <button
                onClick={onCancel}
                className="retro-button retro-button-secondary flex-1 py-2"
                type="button"
              >
                CANCELAR
              </button>

              <button
                onClick={handleConfirm}
                disabled={!!errors.targetSize || !!errors.dim}
                className={`
                  retro-button flex-1 py-2
                  ${errors.targetSize || errors.dim ? 'retro-button-disabled' : 'retro-button-success'}
                `}
                type="button"
              >
                ✓ ENVIAR AGORA
              </button>
            </div>

            <div className="px-4 pb-4">
              <p className="text-xs text-retro-muted text-center">
                Para configuração por arquivo (resolução individual), use{' '}
                <span className="text-retro-accent">UPLOAD AVANÇADO</span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuickConfigModal;
